<!--
  Component: Snapshot

  Main component for School Quality Snapshot (year-versioned)

  Props:
  - dbn - school DBN, passed in from router
  - report_type - school report type, passed in from router
-->

<template>
  <div :class="['report snapshot', {
    'no-selected-school': !hasSelectedSchool
  }]">
    <VueHeadful :title="title"/>
    <div v-if="showLoadingScreen" class="loading-text">Loading...</div>
    <div :class="['report-wrapper',showLoadingScreen?'calc-hidden':'']">
      <div class="print-force-zoom print-only"></div>
      <ReportMenu v-if="hasSelectedSchool"/>
      <ReportHeader :class="[showPKEmbargo?'pk-embargo':'']"/>

      <div class="not-auth-wrapper">
        <NotAuthorized v-if="isUnauthorizedForEmbargo" auth-type="student-profile"/>
      </div>
      <div v-if="!hasSelectedSchool" class="intro-text">
        <h2>Welcome to the Online School Quality Snapshot</h2>
        <p>The School Quality Snapshot has been produced by New York City Public Schools since 2014 to give the general public and parents an easy-to-digest report that highlights key aspects of public schools in NYC. The School Quality Snapshot contains background information about each school, and also strives to present the strengths of the school through multiple measures, including data from the Quality Review, the NYC School Survey, and performance metrics.</p>
      </div>

      <!-- Show these sections while loading so it can calculate sizes of static vizes (not dynamically loaded by metadata) -->
      <FrameworkDetails
        v-show="!loaded || (hasSelectedSchool && !displayNoPKAvailableVersion)"
        :class="hasSelectedSchool?'':'no-print'"/>

      <div v-if="displayNoPKAvailableVersion" class="no-pk-wrapper">
        <p class="no-pk-available-desc">The {{schoolYearRange}} Pre-K Quality Snapshot for this program is not yet available. <LinkExternalUrl :url="previousYearPKUrl">View last year’s Snapshot for this school.</LinkExternalUrl></p>
      </div>
      <!-- Wide footer now shows up when printing and has CSS to NOT break this box between 2 pages -->
      <ReportFooter class="no-print" />
      <ReportFooter
        class="print-version-footer"
      />

      <PrivacyFooter/>
      <BackToTop/>
    </div>
  </div>
</template>

<script>
import url from 'url'
import { mapGetters, mapMutations, mapActions } from 'vuex'
import Component, { mixins } from 'vue-class-component'
import { Watch } from 'vue-property-decorator'

import { REPORT_YEAR, NS_REPORT_GROUP, NS_CURRENT_REPORT } from './const'
import { SQS_EMBARGO_YEAR, SQS_NO_PK_YEAR, SQS_PK_SECRET_KEYS
} from '@/report-settings'
import { RouteMixin } from '@/mixin'

import ReportMenu from './ReportMenu'
import ReportHeader from './ReportHeader'
import FrameworkDetails from './FrameworkDetails'
import ReportFooter from './ReportFooter'
import BackToTop from './BackToTop'
import LinkExternalUrl from '@/components/LinkExternalUrl'

import NotAuthorized from '@/components/NotAuthorized'
import PrivacyFooter from '@/components/PrivacyFooter'

const REPORT_TITLE = 'School Quality Snapshot'

@Component({
  props: {
    pk_secret_key: String,
    dbn: String,
    report_type: String
  },
  computed: {
    ...mapGetters([
      'isLoggedIn'
    ]),
    ...mapGetters(NS_REPORT_GROUP, [
      'reportYear',
      'schoolYearRange',
      'previousYearBaseUrl'
    ]),
    ...mapGetters(NS_CURRENT_REPORT, [
      'isSchoolInfoLoaded',
      'isEmbargoed',
      'isUnauthorizedForEmbargo',
      'selectedSchool',
      'hasSelectedSchool',
      'isSelectedSchoolPK',
      'isCovidYear'
    ])
  },
  methods: {
    ...mapActions([
      'getAuthSPInfo'
    ]),
    ...mapActions(NS_CURRENT_REPORT, [
      'getLayoutMetadata',
      'getSchoolInfo',
      'getSchoolInfoPK'
    ]),
    ...mapMutations(NS_REPORT_GROUP, [
      'setReportYear',
      'setRouteSchool',
      'clearRouteSchool'
    ]),
    ...mapMutations(NS_CURRENT_REPORT, [
      'setIsEmbargoed',
      'setLayoutMetadata',
      'setSchoolInfo',
      'setSchoolInfoPK'
    ])
  },
  components: {
    NotAuthorized,
    ReportMenu,
    ReportHeader,
    FrameworkDetails,
    ReportFooter,
    PrivacyFooter,
    BackToTop,
    LinkExternalUrl
  }
})
export default class Snapshot extends mixins(RouteMixin) {
  loaded = false
  isNoPKAvailable = REPORT_YEAR === SQS_NO_PK_YEAR

  /* Lifecycle */

  mounted () {
    this.checkEmbargo()
    this.checkLogin()
    this.setReportYear(REPORT_YEAR)
    this.updateRouteSchool(this.$route.params)
    this.loadMetadata()
    this.loadSchoolInfo()
    this.loadSchoolInfoPK()
    this.loaded = true
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'auto' // When scroll should pop to location use 'auto'
    })
    // Clear the GA utm parameters after a delay to allow sending GA event
    setTimeout(this.clearRouteQuery, 5000)
  }

  /* Watchers */

  @Watch('selectedSchool')
  onSelectedSchoolChanged (newVal, oldVal) {
    this.checkLogin()
  }

  checkEmbargo () {
    this.setIsEmbargoed(REPORT_YEAR === SQS_EMBARGO_YEAR)
  }

  checkLogin () {
    if (!this.isLoggedIn && this.needLogin) {
      this.getAuthSPInfo()
    }
  }

  clearRouteQuery () {
    // Update the displayed route in URL bar to remove ? params
    const newRoute = this.$_RouteMixin_getRouteCopy()
    if (Object.keys(newRoute.query).length !== 0) {
      newRoute.query = {}
      this.$_RouteMixin_setRoute(newRoute)
    }
  }

  /* Vue Router hooks */

  beforeRouteUpdate (to, from, next) {
    this.updateRouteSchool(to.params)
    next()
  }

  /* Getters */

  get title () {
    let title = `${this.schoolYearRange} ${REPORT_TITLE} - New York City Public Schools`
    if (this.selectedSchool.dbn && this.selectedSchool.report_type) {
      title = `${this.selectedSchool.dbn}/${this.selectedSchool.report_type} - ${title}`
    }
    return title
  }
  get metadataUrl () {
    return url.format(require('./meta/layout_metadata.tsv'))
  }
  get dataPath () {
    return `${this.$STATIC_PATH}/data/${NS_CURRENT_REPORT}`
  }
  get schoolInfoUrl () {
    return `${this.dataPath}/school_info.csv`
  }
  get schoolInfoPKUrl () {
    return `${this.dataPath}/school_info_pk.csv`
  }
  get previousYearPKUrl () {
    return `${this.previousYearBaseUrl}${this.dbn}/PK/`
  }
  get displayNoPKAvailableVersion () {
    return this.isNoPKAvailable && this.isSelectedSchoolPK && !this.showPKEmbargo
  }
  get needLogin () {
    return SQS_PK_SECRET_KEYS[REPORT_YEAR] && this.pkSecretKeyMatch
      ? this.hasSelectedSchool && !this.isSelectedSchoolPK
      : this.isEmbargoed
  }
  get showLoadingScreen () {
    return (this.needLogin && !this.isLoggedIn) || !this.isSchoolInfoLoaded
  }
  get pkSecretKeyMatch () {
    return SQS_PK_SECRET_KEYS[REPORT_YEAR] === this.pk_secret_key
  }
  get showPKEmbargo () {
    return this.isNoPKAvailable && this.pk_secret_key &&
      this.pkSecretKeyMatch
  }

  /* Methods */

  updateRouteSchool (s) {
    if (!s.dbn || !s.report_type) {
      this.clearRouteSchool()
    } else {
      this.setRouteSchool(s)
    }
  }

  loadMetadata () {
    this.getLayoutMetadata(this.metadataUrl)
  }
  loadSchoolInfo () {
    this.getSchoolInfo(this.schoolInfoUrl)
  }
  loadSchoolInfoPK () {
    this.getSchoolInfoPK(this.schoolInfoPKUrl)
  }
}
</script>

<style lang="scss">
</style>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
@import './css/report.scss';
@import './css/const.scss';
.snapshot {
  text-align: left;
  padding-bottom: 20px;
  color: black;
  position: relative;
  /deep/ .privacy-footer {
    /* CSS may be loaded out of order, so override the report-level rule */
    padding: 0px;
  }
  @media print {
    padding-bottom: 0px;
    .report-footer { //new
      page-break-inside: avoid;
    }
  }
}
/* Force report to be wider than it should be, so browser zooms out slightly
    when printing, so content fits on each page */
.print-force-zoom {
  position: absolute;
  width: 100%;
  padding: 0px 24px;
  height: 1px;
}
.intro-text {
  background-color: white;
  padding: 30px 0px 60px;
  h2 {
    font-size: 24px;
  }
  p {
    line-height: 1.5em;
    margin-block-end: 1em;
  }
}
.no-pk-wrapper {
  background: white;
}
.no-pk-available-desc {
  margin-bottom: 0px;
  padding-top: 1em;
  padding-bottom: 1em;
  font-size: 20px;
  font-weight: bold;
}
.not-auth-wrapper {
  background: white;
}
.not-authorized {
  padding-top: 24px;
  padding-bottom:8px;
}
.pk-embargo {
  /deep/ .input-select-year {
    display: none;
  }
}

.print-version-footer {
  visibility: hidden;
  position: absolute;
  word-break: break-word;
  top: 0;
  /deep/ .print-only-inline {
    display: inline;
  }
}

@media print {
  .print-version-footer {
    visibility: visible;
    position: relative;
  }
}

/* Responsive rules */

@media screen and (max-width: $screen-width-1-column-large) {
.no-pk-available-desc,
.not-authorized,
.intro-text > * {
  max-width: $max-width-1-col;
  margin-left: auto;
  margin-right: auto;
}

}
</style>



// WEBPACK FOOTER //
// src/components/sqs/2025/Snapshot.vue